import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  Input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  HsButtonModule,
  HsFormFieldModule,
  HsInputModule,
  HsSelectModule,
  HsSelectOption,
} from '@hsetu/ngui-components';
import { RegistrationType } from '@modules/contact/enums/registration-type.enum';
import { ContactType } from '@hsetu/ngui-views';
import {
  ADD_REGISTRATION_LABEL,
  CUSTOMER_REFERENCE_LABEL,
  DEBTOR_LABEL,
  LID_LABEL,
  VALUE_LABEL,
} from '@modules/contact/i18n/label-text-constants';
import { Registration } from '@modules/contact/models/registration.model';
import { ContactService } from '@modules/contact/services/contact.service';
import { SyncStateType } from '@shared/enums/sync-state-type.enum';
import { GuidGeneratorService } from '@shared/tools/guid-generator.service';

@Component({
  selector: 'kfm-contact-registrations',
  imports: [
    CommonModule,
    FormsModule,
    HsFormFieldModule,
    HsSelectModule,
    HsInputModule,
    HsButtonModule,
  ],
  templateUrl: './kfm-contact-registrations.component.html',
  host: { class: 'kfm-contact-registrations' },
})
export class KfmContactRegistrationsComponent {
  private readonly _guidGenerator = inject(GuidGeneratorService);
  private readonly _contactService = inject(ContactService);

  protected readonly valueFieldLabel = VALUE_LABEL;
  protected readonly customerReferenceLabel = CUSTOMER_REFERENCE_LABEL;
  protected readonly lidLabel = LID_LABEL;
  protected readonly debtorLabel = DEBTOR_LABEL;
  protected readonly addRegistrationLabel = ADD_REGISTRATION_LABEL;

  public readonly contactType = input.required<ContactType>();

  /** The uId of the contact being edited — used to exclude it from duplicate checks. */
  @Input() contactUid = '';

  protected toSelectOption(value: string): HsSelectOption<string> {
    return { label: value, value };
  }

  public readonly registrations = input<Registration[]>([]);

  /** Emits true when any CustomerReference registration number is a duplicate. */
  public readonly hasDuplicateCustomerNumber = output<boolean>();

  /**
   * Reactive mirror of the input array.
   * The input array itself is the source of truth (the parent reads it directly
   * on save). This signal exists only to make Angular's change detection aware
   * of structural changes (add / type-change / value-change).
   */
  protected readonly _registrations = signal<Registration[]>([]);

  /** Maps registration uId → whether its number is a duplicate. */
  protected readonly _duplicates = signal<Record<string, boolean>>({});

  private readonly _allOptions: string[] = Object.keys(RegistrationType)
    .filter(
      (key) =>
        isNaN(Number(key)) && key !== RegistrationType[RegistrationType.None],
    )
    .map((key) =>
      this.getRegistrationValue(
        RegistrationType[key as keyof typeof RegistrationType],
      ),
    );

  protected readonly registrationOptions = computed(
    (): HsSelectOption<string>[] => {
      const used = this._registrations().map((r) =>
        this.getRegistrationValue(r.registrationType!),
      );

      // Determine which options to show based on contact type
      let availableOptions = this._allOptions;
      if (this.contactType() === ContactType.Tenant) {
        // For tenants (mandants), only show VAT
        const vatLabel = this.getRegistrationValue(RegistrationType.VAT);
        availableOptions = this._allOptions.filter(
          (option) => option === vatLabel,
        );
      }

      return availableOptions
        .filter((o) => !used.includes(o))
        .map((label) => ({ value: label, label }));
    },
  );

  constructor() {
    effect(() => {
      this._registrations.set([...this.registrations()]);
    });
  }

  public onAddRegistrationClicked(): void {
    const firstAvailable = this.registrationOptions()[0];
    if (!firstAvailable) return;

    const registration: Registration = {
      registrationType: this.stringToEnum(firstAvailable.value),
      concurrencyIdentificator: this._guidGenerator.emptyUUID,
      concurrencyStamp: new Date().toISOString(),
      contactUId: this._guidGenerator.emptyUUID,
      stampCreate: new Date().toISOString(),
      syncType: SyncStateType.INSERT,
      tenantUId: this._guidGenerator.emptyUUID,
      uId: this._guidGenerator.generateUUID(),
      userUId: this._guidGenerator.emptyUUID,
      registrationNumber: '',
    };

    this.registrations().push(registration);
    this._refresh();
  }

  public onRegistrationTypeChanged(id: string, evt: string): void {
    const entry = this.registrations().find((r) => r.uId === id);
    if (!entry) return;

    entry.registrationType = this.stringToEnum(evt);
    this._refresh();
  }

  public async onRegistrationValueChanged(
    id: string,
    evt: string,
  ): Promise<void> {
    const entry = this.registrations().find((r) => r.uId === id);
    if (!entry) return;

    entry.registrationNumber = evt;
    this._refresh();

    // Only validate duplicates for CustomerReference registrations.
    if (entry.registrationType === RegistrationType.CustomerReference) {
      await this._validateCustomerNumberDuplicate(id, evt);
    }
  }

  public getOptionsForRegistration(
    registration: Registration,
  ): HsSelectOption<string>[] {
    const currentLabel = this.getRegistrationValue(
      registration.registrationType!,
    );
    const available = this.registrationOptions();
    if (available.some((o) => o.value === currentLabel)) return available;
    return [{ value: currentLabel, label: currentLabel }, ...available];
  }

  public getRegistrationValue(registrationType: RegistrationType): string {
    if (registrationType === RegistrationType.LID) {
      return this.lidLabel;
    } else if (registrationType === RegistrationType.CustomerReference) {
      return this.customerReferenceLabel;
    } else if (registrationType === RegistrationType.Debitor) {
      return this.debtorLabel;
    } else return RegistrationType[registrationType];
  }

  protected isDuplicate(registrationUid: string): boolean {
    return this._duplicates()[registrationUid] === true;
  }

  /** Notify Angular about a change in the input array by resetting the signal. */
  private _refresh(): void {
    this._registrations.set([...this.registrations()]);
  }

  private async _validateCustomerNumberDuplicate(
    id: string,
    value: string,
  ): Promise<void> {
    const isDuplicate = value.trim()
      ? await this._contactService.isCustomerNumberDuplicate(
          value,
          this.contactUid,
        )
      : false;

    this._duplicates.update((prev) => ({ ...prev, [id]: isDuplicate }));

    // Emit to parent whether any registration has a duplicate.
    const anyDuplicate = Object.values({
      ...this._duplicates(),
      [id]: isDuplicate,
    }).some(Boolean);
    this.hasDuplicateCustomerNumber.emit(anyDuplicate);
  }

  private stringToEnum(registrationType: string): RegistrationType {
    if (registrationType === this.lidLabel) {
      return RegistrationType.LID;
    } else if (registrationType === this.customerReferenceLabel) {
      return RegistrationType.CustomerReference;
    } else if (registrationType === this.debtorLabel) {
      return RegistrationType.Debitor;
    } else {
      return RegistrationType[
        registrationType as keyof typeof RegistrationType
      ];
    }
  }
}
